/**
 * Wishlist Drawer - mirrors cart drawer pattern
 * Uses WishlistService from wishlist.js
 */

function getEmptyHtml(strings, womensUrl, mensUrl) {
  const emptyTitle = (strings && strings.emptyTitle) || "YOUR WISHLIST IS EMPTY.";
  const shopWomens = (strings && strings.shopWomens) || 'Shop Womens';
  const shopMens = (strings && strings.shopMens) || 'Shop Mens';
  const wUrl = womensUrl || '/collections/womens-best-sellers';
  const mUrl = mensUrl || '/collections/mens-best-sellers';
  return `
  <div class="empty-state-with-trending">
    <div class="cart-empty">
      <svg class="icon icon-wishlist" viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" style="color: #666;">
        <use xlink:href="#icon-wishlist" />
      </svg>
      <strong class="grey empty-state__message empty-state__message--heading">${escapeHtml(emptyTitle)}</strong>
      <div class="empty-state__ctas">
        <a href="${escapeHtml(wUrl)}" class="button button--tonal button--medium">${escapeHtml(shopWomens)}</a>
        <a href="${escapeHtml(mUrl)}" class="button button--tonal button--medium">${escapeHtml(shopMens)}</a>
      </div>
    </div>
  </div>
`;
}

function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents);
  }
  return '$' + (cents / 100).toFixed(2);
}

function renderWishlistPricing(price, compareAtPrice) {
  const onSale = compareAtPrice > 0 && compareAtPrice > price;
  if (onSale) {
    return `<div class="cart-item__discounted-prices">
      <s class="cart-item__old-price cart__line-item--price">${formatMoney(compareAtPrice)}</s>
      <strong class="cart-item__final-price cart__line-item--price cart-item__final-price--red">${formatMoney(price)}</strong>
    </div>`;
  }
  return `<div class="cart__line-item--price">${formatMoney(price || 0)}</div>`;
}

function getShopifyRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || (window.shopUrl ? window.shopUrl + '/' : '/');
}

async function fetchFreshAvailability(items, root) {
  const itemsWithHandle = items.filter((i) => i.handle && i.variant_id);
  const uniqueHandles = [...new Set(itemsWithHandle.map((i) => i.handle))];
  const productByHandle = {};

  await Promise.all(
    uniqueHandles.map(async (handle) => {
      try {
        const res = await fetch(`${root}products/${handle}.js`);
        if (res.ok) {
          productByHandle[handle] = await res.json();
        }
      } catch (_) {
        /* keep section/Liquid availability for this handle */
      }
    })
  );

  const updates = [];
  for (const item of itemsWithHandle) {
    const product = productByHandle[item.handle];
    if (!product) continue;
    const variant = product.variants && product.variants.find((v) => String(v.id) === String(item.variant_id));
    if (!variant) continue;
    updates.push({
      product_id: item.product_id,
      variant_id: item.variant_id,
      available: variant.available,
      price: variant.price,
      compare_at_price: variant.compare_at_price
    });
  }
  return updates;
}

function applyAvailabilityToDom(container, availabilityMap, strings) {
  if (!container) return;
  const addToCartText = (strings && strings.addToCart) || '+ Add to cart';
  const outOfStockText = (strings && strings.outOfStock) || 'Out of stock';

  container.querySelectorAll('.wishlist-line-item').forEach((lineItem) => {
    const productId = lineItem.dataset && lineItem.dataset.productId;
    const update = productId ? availabilityMap.get(String(productId)) : null;
    if (!update) return;

    const isAvailable = update.available === true;
    lineItem.dataset.available = isAvailable ? 'true' : 'false';

    const btn = lineItem.querySelector('[data-wishlist-add-to-cart]');
    if (!btn) return;

    if (isAvailable) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-label');
      btn.dataset.available = 'true';
      btn.textContent = addToCartText;
    } else {
      const displayName = lineItem.querySelector('.name')?.textContent || '';
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-label', `${displayName} - ${outOfStockText}`);
      btn.dataset.available = 'false';
      btn.textContent = outOfStockText;
    }

    if (update.price != null) {
      const pricingEl = lineItem.querySelector('.cart__line-item--pricing');
      if (pricingEl) {
        pricingEl.innerHTML = renderWishlistPricing(update.price, update.compare_at_price || 0);
      }
    }
  });
}

function renderWishlistItem(item, strings) {
  const addToCartText = (strings && strings.addToCart) || '+ Add to cart';
  const outOfStockText = (strings && strings.outOfStock) || 'Out of stock';
  const removeLabel = (strings && strings.remove) || 'Remove';
  const url = item.url || `/products/${item.handle}`;
  const displayName = item.display_name || (item.title && item.title.includes(' | ') ? item.title.split(' | ')[0].trim() : null) || item.title || '';
  const variantDisplay = item.variant || (item.title && item.title.includes(' | ') ? item.title.split(' | ')[1].trim() : null) || '';
  const img = item.image ? `<img src="${item.image}" alt="${escapeHtml(displayName)}" width="64" height="80" loading="lazy" class="placeholder">` : '<div class="placeholder" style="width:64px;height:80px;background:#eee;"></div>';
  const isAvailable = item.available !== false;
  const btnContent = isAvailable ? escapeHtml(addToCartText) : escapeHtml(outOfStockText);
  const btnAttrs = isAvailable
    ? `data-wishlist-add-to-cart data-variant-id="${item.variant_id || ''}" data-available="true"`
    : `disabled aria-disabled="true" aria-label="${escapeHtml(displayName)} - ${escapeHtml(outOfStockText)}" data-wishlist-add-to-cart data-variant-id="${item.variant_id || ''}" data-available="false"`;
  return `
    <div class="cart__line-item wishlist-line-item" data-product-id="${item.product_id}" data-variant-id="${item.variant_id || ''}" data-available="${isAvailable}">
      <a class="cart__line-item--image" href="${escapeHtml(url)}" aria-label="${escapeHtml(displayName)}">${img}</a>
      <div class="cart__line-item--info line-height-1">
        <div class="cart__line-item--top">
          <div class="cart__line-item--title-group">
            <div class="lead-text bold-700">
              <a href="${escapeHtml(url)}" class="name">${escapeHtml(displayName)}</a>
            </div>
            ${variantDisplay ? `<div class="small-text grey cart__line-item--variant">${escapeHtml(variantDisplay)}</div>` : ''}
          </div>
          <div class="cart__line-item--remove">
            <button type="button" class="button button--icon" aria-label="${removeLabel}: ${escapeHtml(displayName)}" data-wishlist-remove>
              <span class="remove-icon">${getRemoveIconSvg()}</span>
            </button>
          </div>
        </div>
        <div class="cart__line-item--bottom">
          <div class="cart__line-item--pricing">
            ${renderWishlistPricing(item.price || 0, item.compare_at_price || 0)}
          </div>
          <button type="button" class="wishlist-line-item__add-to-cart button button--small button--default" ${btnAttrs}>${btnContent}</button>
        </div>
      </div>
    </div>
  `;
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function getRemoveIconSvg() {
  return '<svg class="icon icon-remove" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" stroke="currentColor" style="color: #000;"><use xlink:href="#icon-remove" /></svg>';
}

function updateWishlistIcon() {
  const toggle = document.getElementById('wishlist-toggle');
  if (!toggle) return;
  // Defer update while nav pulse animation runs so fill animation isn't interrupted
  if (toggle.classList.contains('wishlist-heart--animating')) {
    setTimeout(updateWishlistIcon, 460);
    return;
  }
  // Use toggle.querySelector to avoid matching the icon-defs symbol (id="icon-wishlist")
  let icon = toggle.querySelector('svg');
  const count = window.WishlistService ? window.WishlistService.count() : 0;
  const isFull = count > 0;

  if (!icon) {
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.id = 'nav-icon-wishlist';
    svg.setAttribute('class', 'icon-wishlist');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.setAttribute('width', '28');
    svg.setAttribute('fill', isFull ? 'currentColor' : 'none');
    svg.setAttribute('stroke', 'currentColor');
    const titleEl = document.createElementNS(svgNS, 'title');
    titleEl.textContent = 'Wishlist';
    svg.appendChild(titleEl);
    toggle.appendChild(svg);
    icon = svg;
  }
  if (!icon) return;

  icon.setAttribute('fill', isFull ? 'currentColor' : 'none');
  icon.setAttribute('stroke', 'currentColor');
  const svgNS = 'http://www.w3.org/2000/svg';
  const xlinkNS = 'http://www.w3.org/1999/xlink';
  const title = icon.querySelector('title');
  icon.textContent = '';
  if (title) icon.appendChild(title);
  if (isFull) {
    const use = document.createElementNS(svgNS, 'use');
    use.setAttributeNS(xlinkNS, 'href', '#icon-wishlist-full');
    icon.appendChild(use);
  } else {
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('class', 'icon-wishlist-path');
    path.setAttribute('d', 'M16 10.259C13.3333 3.99978 4 4.66645 4 12.6665C4 20.6665 16 27.3334 16 27.3334C16 27.3334 28 20.6665 28 12.6665C28 4.66645 18.6667 3.99978 16 10.259Z');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2.25');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    icon.appendChild(path);
  }
}

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.overlay = null;
    this.drawer = null;
    this.toggle = null;
    this.itemsContainer = null;
    this.footer = null;
    this.loadingEl = null;
  }

  connectedCallback() {
    this.drawer = this.querySelector('#wishlist-drawer') || document.getElementById('wishlist-drawer');
    this.toggle = document.getElementById('wishlist-toggle');
    this.overlay = document.getElementById('overlay');
    this.itemsContainer = this.querySelector('#wishlist-drawer-items') || document.getElementById('wishlist-drawer-items');
    this.footer = this.querySelector('#wishlist-drawer-footer') || document.getElementById('wishlist-drawer-footer');
    this.loadingEl = this.querySelector('#wishlist-loading') || document.getElementById('wishlist-loading');

    if (!this.drawer || !this.itemsContainer) return;
    document.addEventListener('keydown', this.onDocumentKeydown);

    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    this.querySelectorAll('.wishlist-drawer__close').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        if (this.overlay && this.overlay.classList.contains('wishlist-overlay')) this.close();
      });
    }

    if (this.footer) {
      this.footer.addEventListener('click', (e) => {
        if (e.target.closest('[data-wishlist-add-all]')) {
          e.preventDefault();
          this.handleAddAllToCart(e.target.closest('[data-wishlist-add-all]'));
        }
      });
    }

    this.itemsContainer.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      const addBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if (removeBtn) {
        e.preventDefault();
        const lineItem = removeBtn.closest('.wishlist-line-item');
        const productId = lineItem && lineItem.dataset && lineItem.dataset.productId;
        if (productId) this.handleRemove(productId);
      } else if (addBtn) {
        e.preventDefault();
        const lineItem = addBtn.closest('.wishlist-line-item');
        const variantId = addBtn.dataset && addBtn.dataset.variantId;
        const productId = lineItem && lineItem.dataset && lineItem.dataset.productId;
        const item = lineItem && window.WishlistService ? window.WishlistService.get().find((i) => String(i.product_id) === String(productId)) : null;
        if (variantId) {
          this.handleAddToCart(variantId, addBtn, productId);
        } else if (item && item.handle) {
          this.handleAddToCartByHandle(item, addBtn, productId);
        }
      }
    });

    this.onWishlistUpdated = () => {
      if (this.drawer && this.drawer.classList.contains('active')) {
        this.refreshItems();
      } else {
        this.render();
      }
    };
    document.addEventListener(
      (window.WishlistService && window.WishlistService.WISHLIST_UPDATED) || 'wishlist:updated',
      this.onWishlistUpdated
    );
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    if (this.onWishlistUpdated) {
      document.removeEventListener(
        (window.WishlistService && window.WishlistService.WISHLIST_UPDATED) || 'wishlist:updated',
        this.onWishlistUpdated
      );
    }
  }

  updateBadge() {
    updateWishlistIcon();
  }

  setLoading(show) {
    if (!this.loadingEl) return;
    this.loadingEl.hidden = !show;
    if (this.itemsContainer) this.itemsContainer.hidden = show;
    if (this.footer) {
      this.footer.hidden = show || this.itemsContainer?.dataset.empty === 'true';
    }
  }

  render() {
    if (!this.itemsContainer) return;
    const items = window.WishlistService ? window.WishlistService.get() : [];
    const strings = window.wishlistStrings || {};
    const womensUrl = this.dataset && this.dataset.womensUrl;
    const mensUrl = this.dataset && this.dataset.mensUrl;
    const trendingEl = document.getElementById('wishlist-drawer-trending');
    if (items.length === 0) {
      this.itemsContainer.innerHTML = getEmptyHtml(strings, womensUrl, mensUrl);
      this.itemsContainer.dataset.empty = 'true';
      if (this.footer) this.footer.hidden = true;
      if (trendingEl) trendingEl.hidden = false;
    } else {
      this.itemsContainer.innerHTML = items.map((item) => renderWishlistItem(item, strings)).join('');
      this.itemsContainer.dataset.empty = 'false';
      if (this.footer) this.footer.hidden = false;
      if (trendingEl) trendingEl.hidden = true;
    }
    if (this.updateBadge) this.updateBadge();
  }

  async refreshItems() {
    if (!this.itemsContainer) return false;

    const items = window.WishlistService ? window.WishlistService.get() : [];
    const strings = window.wishlistStrings || {};
    const womensUrl = this.dataset && this.dataset.womensUrl;
    const mensUrl = this.dataset && this.dataset.mensUrl;
    const trendingEl = document.getElementById('wishlist-drawer-trending');

    if (items.length === 0) {
      this.render();
      return false;
    }

    const sectionItems = items.filter((i) => i.handle && i.variant_id);
    const legacyItems = items.filter((i) => !i.handle || !i.variant_id);
    const root = getShopifyRoot();
    let useSectionHtml = false;

    if (sectionItems.length > 0) {
      try {
        const productIds = sectionItems.map((i) => i.product_id).join(',');
        const handles = sectionItems.map((i) => i.handle).join(',');
        const variantIds = sectionItems.map((i) => i.variant_id).join(',');
        const url = `${root}?section_id=wishlist-drawer-items&product_ids=${encodeURIComponent(productIds)}&product_handles=${encodeURIComponent(handles)}&variant_ids=${encodeURIComponent(variantIds)}`;
        const response = await fetch(url);
        if (response.ok) {
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const sectionEl = doc.querySelector('[data-section-type="wishlist-drawer-items"]') || doc.getElementById('shopify-section-wishlist-drawer-items');
          const removeScript = doc.querySelector('script[data-wishlist-remove-ids]');
          if (sectionEl) {
            const sectionHtml = sectionEl.innerHTML;
            const hasLineItems = sectionHtml.includes('wishlist-line-item');
            if (hasLineItems || legacyItems.length > 0) {
              let removeIds = [];
              if (removeScript && removeScript.textContent) {
                try {
                  removeIds = JSON.parse(removeScript.textContent.trim());
                } catch (_) {}
              }
              removeIds.forEach((id) => {
                if (window.WishlistService) window.WishlistService.remove(String(id), { silent: true });
              });
              const legacyHtml = legacyItems.map((item) => renderWishlistItem(item, strings)).join('');
              this.itemsContainer.innerHTML = sectionHtml + legacyHtml;
              this.itemsContainer.dataset.empty = 'false';
              if (this.footer) this.footer.hidden = false;
              if (trendingEl) trendingEl.hidden = true;
              useSectionHtml = true;
            }
          }
        }
      } catch (err) {
        console.error('WishlistDrawer: section fetch failed', err);
      }
    }

    if (!useSectionHtml) {
      this.render();
    }

    const currentItems = window.WishlistService ? window.WishlistService.get() : [];
    const updates = await fetchFreshAvailability(currentItems, root);
    if (updates.length > 0 && window.WishlistService && window.WishlistService.syncAvailability) {
      window.WishlistService.syncAvailability(updates);
      const availabilityMap = new Map(updates.map((u) => [String(u.product_id), u]));
      applyAvailabilityToDom(this.itemsContainer, availabilityMap, strings);
    }

    if (this.updateBadge) this.updateBadge();
    return useSectionHtml;
  }

  async open() {
    if (!this.drawer) return;
    const wasActive = this.drawer.classList.contains('active');
    const nav = document.querySelector('nav-drawer');
    if (nav && nav.mobileCollapse) nav.mobileCollapse();

    const items = window.WishlistService ? window.WishlistService.get() : [];
    const hasItems = items.length > 0;

    this.drawer.classList.add('animate', 'active');
    this.drawer.style.visibility = 'visible';
    if (this.overlay) this.overlay.classList.add('active', 'wishlist-overlay');
    setAriaExpanded(this.toggle, true);
    if (!wasActive && typeof lockBodyScroll === 'function') lockBodyScroll();
    this.drawer.focus();
    if (typeof trapFocus === 'function') trapFocus(this.drawer);

    if (hasItems) {
      this.setLoading(true);
      try {
        await this.refreshItems();
      } finally {
        this.setLoading(false);
      }
    } else {
      this.render();
    }
  }

  close() {
    if (!this.drawer) return;
    const wasActive = this.drawer.classList.contains('active');
    this.setLoading(false);
    if (this.itemsContainer) this.itemsContainer.hidden = false;
    this.drawer.classList.remove('active');
    if (this.overlay) {
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        this.overlay.classList.remove('wishlist-overlay', 'overlay-fading');
      }, 300);
    }
    setAriaExpanded(this.toggle, false);
    if (wasActive && typeof unlockBodyScroll === 'function') unlockBodyScroll();
    if (typeof removeTrapFocus === 'function' && this.toggle) removeTrapFocus(this.toggle);
  }

  handleDocumentKeydown(e) {
    if (e.key !== 'Escape' && e.code !== 'Escape') return;
    if (!this.drawer || !this.drawer.classList.contains('active')) return;
    e.preventDefault();
    this.close();
  }

  handleRemove(productId) {
    if (window.WishlistService) {
      const item = window.WishlistService.get().find((i) => String(i.product_id) === String(productId));
      if (item) window.WishlistService.trackRemoved(item);
      window.WishlistService.remove(productId);
    }
  }

  async handleAddToCart(variantId, button, productId) {
    if (!variantId) return;
    const lineItem = button.closest('.wishlist-line-item');
    const isAvailable = lineItem && lineItem.dataset && lineItem.dataset.available !== 'false';
    const btnAvailable = button.dataset && button.dataset.available !== 'false';
    if (!isAvailable || !btnAvailable) return;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';
    try {
      const root = getShopifyRoot();
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      const data = await response.json();
      if (data.status && data.status !== 200) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }
      // Cart changed: clear the cached cart so the drawer reads fresh data
      if (typeof invalidateCartCache === 'function') invalidateCartCache();
      this.close();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.open) await cartDrawer.open(true);
      if (productId && window.WishlistService) {
        window.WishlistService.remove(productId);
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to add to cart');
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async handleAddToCartByHandle(item, button, productId) {
    try {
      const root = getShopifyRoot();
      const res = await fetch(root + `products/${item.handle}.js`);
      const product = await res.json();
      const variant = (product.variants && product.variants.find((v) => v.available)) || (product.variants && product.variants[0]);
      if (variant && variant.id) {
        await this.handleAddToCart(variant.id, button, productId || item.product_id);
      } else {
        throw new Error('Product unavailable');
      }
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to add to cart');
    } finally {
      button.disabled = false;
      button.textContent = '+ Add to cart';
    }
  }

  async handleAddAllToCart(button) {
    const root = getShopifyRoot();
    const cartItems = [];
    const drawerOpen = this.drawer && this.drawer.classList.contains('active');

    if (drawerOpen && this.itemsContainer) {
      this.itemsContainer.querySelectorAll('.wishlist-line-item').forEach((lineItem) => {
        if (lineItem.dataset && lineItem.dataset.available === 'false') return;
        const variantId = lineItem.dataset && lineItem.dataset.variantId;
        if (variantId) cartItems.push({ id: variantId, quantity: 1 });
      });
    } else {
      const items = window.WishlistService ? window.WishlistService.get() : [];
      if (items.length === 0) return;

      const availableItems = items.filter((i) => i.available !== false);
      for (const item of availableItems) {
        if (item.variant_id) {
          cartItems.push({ id: item.variant_id, quantity: 1 });
        } else if (item.handle) {
          try {
            const res = await fetch(root + `products/${item.handle}.js`);
            const product = await res.json();
            const variant = (product.variants && product.variants.find((v) => v.available)) || (product.variants && product.variants[0]);
            if (variant && variant.id) cartItems.push({ id: variant.id, quantity: 1 });
          } catch {
            /* skip unavailable */
          }
        }
      }
    }

    if (cartItems.length === 0) {
      alert('No items could be added to cart.');
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });
      const data = await response.json();

      if (data.status && data.status !== 200) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      this.close();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.open) await cartDrawer.open(true);
      if (window.WishlistService) {
        window.WishlistService.set([]);
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to add to cart');
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);

/**
 * Wishlist button init - delegated click + sync active state
 */
function initWishlistButtons() {
  let lastWishlistCount = 0;
  let hasInitialized = false;

  function triggerNavAnimation() {
    const navToggle = document.getElementById('wishlist-toggle');
    if (!navToggle) return;
    navToggle.classList.remove('wishlist-heart--animating');
    requestAnimationFrame(() => {
      navToggle.classList.add('wishlist-heart--animating');
      setTimeout(() => navToggle.classList.remove('wishlist-heart--animating'), 450);
    });
  }

  function updateButtonStates() {
    const svc = window.WishlistService;
    if (!svc) return;
    const count = svc.count();
    const justAdded = !hasInitialized ? false : count > lastWishlistCount;
    if (!hasInitialized) {
      hasInitialized = true;
    } else if (justAdded) {
      triggerNavAnimation();
    }
    lastWishlistCount = count;
    document.querySelectorAll('[data-wishlist-button]').forEach((btn) => {
      const id = btn.dataset && btn.dataset.productId;
      const isActive = id ? svc.has(id) : false;
      btn.classList.toggle('wishlist-heart--active', isActive);
      btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
    });
    // Delay icon update until after nav pulse animation (450ms)
    if (justAdded) {
      setTimeout(updateWishlistIcon, 450);
    } else {
      updateWishlistIcon();
    }
  }

  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-button]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const svc = window.WishlistService;
    if (!svc) return;
    const productId = btn.dataset && btn.dataset.productId;
    const variantId = btn.dataset && btn.dataset.variantId;
    const handle = btn.dataset && btn.dataset.productHandle;
    const title = btn.dataset && btn.dataset.productTitle;
    const displayName = (btn.dataset && btn.dataset.productDisplayName) || title;
    const variant = (btn.dataset && btn.dataset.productVariant) || '';
    const url = btn.dataset && btn.dataset.productUrl;
    const image = btn.dataset && btn.dataset.productImage;
    const price = (btn.dataset && btn.dataset.productPrice) ? parseInt(btn.dataset.productPrice, 10) : 0;
    const compareAtPrice = (btn.dataset && btn.dataset.productCompareAtPrice) ? parseInt(btn.dataset.productCompareAtPrice, 10) : 0;
    const fullUrl = url && !url.startsWith('http') ? (window.shopUrl || '') + url : url;
    const available = btn.dataset && btn.dataset.variantAvailable === 'true';
    const item = { product_id: productId, variant_id: variantId, handle, title, display_name: displayName, variant, url: fullUrl, image, price, compare_at_price: compareAtPrice, available };
    const wasAdded = svc.has(productId);
    if (wasAdded) {
      svc.remove(productId);
      svc.trackRemoved({ title });
      btn.classList.remove('wishlist-heart--active');
      btn.setAttribute('aria-label', 'Add to wishlist');
    } else {
      const hasSizeOptions = btn.dataset && btn.dataset.productHasSizeOptions === 'true';
      const canOpenModal = hasSizeOptions && handle && window.WishlistQuickViewService;
      const modalExists = canOpenModal && document.getElementById('product-quick-view-modal');

      if (canOpenModal && modalExists) {
        window.WishlistQuickViewService.open(handle, btn);
      } else {
        const added = svc.add(item);
        if (added) {
          svc.trackAdded({ ...item, id: productId, variant: { id: variantId, price }, selected_or_first_available_variant: { id: variantId, price } });
          btn.classList.add('wishlist-heart--active');
          btn.setAttribute('aria-label', 'Remove from wishlist');
        }
      }
    }
    updateButtonStates();
  });

  document.addEventListener((window.WishlistService && window.WishlistService.WISHLIST_UPDATED) || 'wishlist:updated', updateButtonStates);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateButtonStates);
  } else {
    updateButtonStates();
  }
}

/**
 * WishlistQuickViewService - opens modal for size selection before adding to wishlist
 */
window.WishlistQuickViewService = {
  modal: null,
  modalContent: null,
  skeleton: null,
  loadingOverlay: null,
  closeButtonHTML: null,
  triggerButton: null,

  init() {
    this.modal = document.getElementById('product-quick-view-modal');
    this.modalContent = document.getElementById('product-quick-view-content');
    this.skeleton = document.getElementById('product-quick-view-skeleton');
    this.loadingOverlay = this.modal && this.modal.querySelector('.product-quick-view-modal__loading-overlay');
    const closeBtn = this.modalContent && this.modalContent.querySelector('.product-quick-view-modal__close-button');
    this.closeButtonHTML = closeBtn ? closeBtn.outerHTML : null;
    this.skeletonHTML = this.skeleton ? this.skeleton.outerHTML : '';
  },

  async open(productHandle, triggerButton) {
    if (!this.modal || !this.modalContent) this.init();
    if (!this.modal || !this.modalContent || !productHandle) return;

    this.triggerButton = triggerButton;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || (window.shopUrl ? window.shopUrl + '/' : '/');

    this.modalContent.innerHTML = (this.closeButtonHTML || '') + (this.skeletonHTML || '');
    if (this.loadingOverlay) this.loadingOverlay.classList.remove('hidden');
    this.modal.classList.add('loading');
    this.modal.open(false);

    try {
      const res = await fetch(root + `products/${productHandle}?section_id=wishlist-quick-view`);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const section = doc.querySelector('[data-wishlist-quick-view]');
      if (!section) throw new Error('Wishlist quick view content not found');

      this.modalContent.innerHTML = (this.closeButtonHTML || '') + section.outerHTML;
      if (this.loadingOverlay) this.loadingOverlay.classList.add('hidden');
      this.modal.classList.remove('loading');
      this.modal.classList.add('animating-in');

      this.attachHandlers();
    } catch (err) {
      console.error('WishlistQuickViewService:', err);
      if (this.skeleton) this.skeleton.classList.add('hidden');
      if (this.loadingOverlay) this.loadingOverlay.classList.add('hidden');
      this.modal.classList.remove('loading');
      this.modal.close();
    }
  },

  attachHandlers() {
    const container = this.modalContent.querySelector('[data-wishlist-quick-view]');
    if (!container) return;

    const selector = this.modalContent.querySelector('[data-wishlist-quick-selector]');
    const variantInput = this.modalContent.querySelector('[data-wishlist-quick-variant-input]');
    const addBtn = this.modalContent.querySelector('[data-wishlist-quick-add]');
    const variantError = this.modalContent.querySelector('[data-wishlist-quick-error]');
    const priceEl = this.modalContent.querySelector('[data-wishlist-quick-price]');

    if (selector && variantInput) {
      selector.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', function () {
          const variantId = this.dataset.variantId;
          const label = this.closest('label');
          const isAvailable = label && label.dataset && label.dataset.variantAvailable === 'true';
          const price = (label && label.dataset && label.dataset.variantPrice) ? parseInt(label.dataset.variantPrice, 10) : 0;
          const compareAtPrice = (label && label.dataset && label.dataset.variantCompareAtPrice) ? parseInt(label.dataset.variantCompareAtPrice, 10) : 0;
          if (variantId) {
            variantInput.value = variantId;
            if (variantError) variantError.hidden = true;
            if (priceEl && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
              const onSale = compareAtPrice > 0 && compareAtPrice > price;
              const priceHtml = Shopify.formatMoney(price);
              const compareAtPriceHtml = compareAtPrice > 0 ? Shopify.formatMoney(compareAtPrice) : '';
              const priceClasses = onSale ? 'price price--on-sale' : 'price';
              let html = `<div class="${priceClasses}"><div class="price__container">`;
              html += `<div class="price__regular"><span class="visually-hidden visually-hidden--inline">Regular price</span><span class="price-item price-item--regular">${priceHtml}</span></div>`;
              html += '<div class="price__sale">';
              if (onSale) {
                html += `<span class="visually-hidden visually-hidden--inline">Regular price</span><span><s class="price-item price-item--regular">${compareAtPriceHtml}</s></span>`;
              }
              html += `<span class="visually-hidden visually-hidden--inline">Sale price</span><span class="price-item price-item--sale">${priceHtml}</span></div></div></div>`;
              priceEl.innerHTML = html;
            }
          }
        });
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', () => this.handleAddToWishlist(container, addBtn));
    }
  },

  handleAddToWishlist(container, addBtn) {
    const svc = window.WishlistService;
    if (!svc) return;

    const variantInput = this.modalContent.querySelector('[data-wishlist-quick-variant-input]');
    const variantError = this.modalContent.querySelector('[data-wishlist-quick-error]');
    const variantId = variantInput && variantInput.value;
    if (!variantId) {
      if (variantError) variantError.hidden = false;
      return;
    }

    const checkedRadio = this.modalContent.querySelector('[data-wishlist-quick-selector] input[type="radio"]:checked');
    const priceLabel = checkedRadio && checkedRadio.closest('label');
    const price = (priceLabel && priceLabel.dataset && priceLabel.dataset.variantPrice) ? parseInt(priceLabel.dataset.variantPrice, 10) : (container.dataset && container.dataset.productPrice ? parseInt(container.dataset.productPrice, 10) : 0);
    const compareAtPrice = (priceLabel && priceLabel.dataset && priceLabel.dataset.variantCompareAtPrice) ? parseInt(priceLabel.dataset.variantCompareAtPrice, 10) : (container.dataset && container.dataset.productCompareAtPrice ? parseInt(container.dataset.productCompareAtPrice, 10) : 0);
    const isAvailable = priceLabel && priceLabel.dataset && priceLabel.dataset.variantAvailable === 'true';

    const productId = container.dataset && container.dataset.productId;
    const handle = container.dataset && container.dataset.productHandle;
    const title = container.dataset && container.dataset.productTitle;
    const displayName = (container.dataset && container.dataset.productDisplayName) || title;
    const colorLabel = (container.dataset && container.dataset.productVariant) || '';
    const sizeValue = checkedRadio ? checkedRadio.value : '';
    const variant = (colorLabel && sizeValue) ? colorLabel + ' • ' + sizeValue : (colorLabel || sizeValue || '');
    const url = container.dataset && container.dataset.productUrl;
    const image = container.dataset && container.dataset.productImage;
    const fullUrl = url && !url.startsWith('http') ? (window.shopUrl || '') + url : url;

    const item = {
      product_id: productId,
      variant_id: variantId,
      handle,
      title,
      display_name: displayName,
      variant,
      url: fullUrl,
      image,
      price,
      compare_at_price: compareAtPrice,
      available: isAvailable
    };

    const added = svc.add(item);
    if (added) {
      svc.trackAdded({
        ...item,
        id: productId,
        variant: { id: variantId, price },
        selected_or_first_available_variant: { id: variantId, price }
      });
    }

    this.modal.close();
    document.querySelectorAll('[data-wishlist-button]').forEach((btn) => {
      const id = btn.dataset && btn.dataset.productId;
      if (id && svc.has(id)) {
        btn.classList.add('wishlist-heart--active');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      }
    });
    // Let wishlist:updated -> updateButtonStates handle nav icon (delayed for animation)
    if (this.triggerButton) {
      this.triggerButton.classList.add('wishlist-heart--active');
      this.triggerButton.setAttribute('aria-label', 'Remove from wishlist');
    }
    document.dispatchEvent(new CustomEvent((window.WishlistService && window.WishlistService.WISHLIST_UPDATED) || 'wishlist:updated'));
  }
};

function initWishlist() {
  window.WishlistQuickViewService.init();
  initWishlistButtons();
  updateWishlistIcon();
  requestAnimationFrame(() => updateWishlistIcon());
  setTimeout(() => updateWishlistIcon(), 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  initWishlist();
}
